import { useCallback } from 'react';
import {
  SuspenseQueryHookFetchPolicy,
  useQuery,
  useSuspenseQuery,
  WatchQueryFetchPolicy,
} from '@apollo/client';
import { useSelector } from 'react-redux';

import { BaseEmployerCommerceInput } from '../../modules/EmployerCommerce/types';
import { getSessionCompanyId } from '../../selectors/session';
import { getExpiryInDays } from '../helpers/monetisationUtils';
import { CurrentCompanyJobSlotsSubscription } from '../interfaces/entities';
import { getCurrentCompanyJobSlotsSubscriptionQuery } from './graphql';
import { useUpdateUserCompanyJobPostModeState } from './useUpdateUserCompanyJobPostModeState';

type QueryVariables = {
  input: BaseEmployerCommerceInput;
};
type QueryResponse = {
  getCurrentCompanyJobSlotsSubscription: CurrentCompanyJobSlotsSubscription;
};

type UseGetCurrentCompanyJobSlotsSubscription = {
  cacheTypePolicy?: WatchQueryFetchPolicy;
  onCompleted?: (
    data: CurrentCompanyJobSlotsSubscription & {
      isSubscriptionPlanExpiring: boolean;
    }
  ) => void;
};

const SUBSCRIPTION_EXPIRING_BANNER_THRESHOLD_DAYS = 7;
function isSubscriptionPlanExpiring(expiryDateTime: string | undefined) {
  if (!expiryDateTime) {
    return false;
  }
  const expiryInDays = getExpiryInDays(expiryDateTime);
  return (
    expiryInDays >= 0 &&
    expiryInDays <= SUBSCRIPTION_EXPIRING_BANNER_THRESHOLD_DAYS
  );
}

export const useGetCurrentCompanyJobSlotsSubscription = (
  { cacheTypePolicy, onCompleted }: UseGetCurrentCompanyJobSlotsSubscription = {
    cacheTypePolicy: undefined,
  }
) => {
  const sessionCompanyId: string = useSelector(getSessionCompanyId);
  const { data, loading, error, refetch } = useQuery<
    QueryResponse,
    QueryVariables
  >(getCurrentCompanyJobSlotsSubscriptionQuery, {
    variables: {
      input: {
        companyId: sessionCompanyId,
      },
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: cacheTypePolicy || 'network-only',
    onCompleted: data => {
      onCompleted?.({
        ...data.getCurrentCompanyJobSlotsSubscription,
        isSubscriptionPlanExpiring: isSubscriptionPlanExpiring(
          data.getCurrentCompanyJobSlotsSubscription.expiryDateTime
        ),
      });
    },
  });

  useUpdateUserCompanyJobPostModeState({
    currentCompanyJobSlotsSubscription:
      data?.getCurrentCompanyJobSlotsSubscription,
    sessionCompanyId,
  });

  const refetchGetCurrentCompanyJobSlotsSubscription = useCallback(() => {
    refetch();
  }, [refetch]);

  const isPlanExpiring = isSubscriptionPlanExpiring(
    data?.getCurrentCompanyJobSlotsSubscription?.expiryDateTime
  );

  return {
    currentCompanyJobSlotsSubscriptionData:
      data?.getCurrentCompanyJobSlotsSubscription,
    isSubscriptionPlanExpiring: isPlanExpiring,
    loading,
    error,
    refetchGetCurrentCompanyJobSlotsSubscription,
  } as const;
};

export const useGetCurrentCompanyJobSlotsSubscriptionSuspenseQuery =
  (options?: {
    queryKey?: string;
    fetchPolicy?: SuspenseQueryHookFetchPolicy;
  }) => {
    const { queryKey, fetchPolicy } = options || {};
    const sessionCompanyId: string = useSelector(getSessionCompanyId);
    const { data } = useSuspenseQuery<QueryResponse, QueryVariables>(
      getCurrentCompanyJobSlotsSubscriptionQuery,
      {
        variables: {
          input: {
            companyId: sessionCompanyId,
          },
        },
        fetchPolicy: fetchPolicy || 'cache-and-network',
        queryKey: queryKey || 'getCurrentCompanyJobSlotsSubscription',
      }
    );

    useUpdateUserCompanyJobPostModeState({
      currentCompanyJobSlotsSubscription:
        data?.getCurrentCompanyJobSlotsSubscription,
      sessionCompanyId,
    });

    const isPlanExpiring = isSubscriptionPlanExpiring(
      data?.getCurrentCompanyJobSlotsSubscription?.expiryDateTime
    );

    return {
      currentCompanyJobSlotsSubscriptionData:
        data.getCurrentCompanyJobSlotsSubscription,
      isSubscriptionPlanExpiring: isPlanExpiring,
    } as const;
  };
